import React, { useCallback, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 6;
const EDGE = 8;

/** Where a panel of `size` goes next to `rect`: below it, or above when there is no room below. */
export function floatingPosition(rect, size, viewport, align = 'left') {
  const below = rect.bottom + GAP;
  const above = rect.top - GAP - size.height;
  const top = below + size.height <= viewport.height - EDGE || above < EDGE ? below : above;
  const left = align === 'right' ? rect.right - size.width : rect.left;
  return {
    top: Math.round(Math.max(EDGE, Math.min(top, viewport.height - EDGE - size.height))),
    left: Math.round(Math.max(EDGE, Math.min(left, viewport.width - EDGE - size.width))),
  };
}

/**
 * A menu or popover drawn at the top of the page (portal, position: fixed), pinned to its trigger.
 * Track rows are separate stacking layers inside a scrolling area, so a panel drawn inside one
 * row was covered by the rows below it and clipped at the bottom of the timeline.
 */
export const FloatingPanel = React.forwardRef(({ anchorRef, align = 'left', className, testId, children }, ref) => {
  const [node, setNode] = useState(null);
  const [position, setPosition] = useState(null);
  const attach = useCallback((element) => {
    setNode(element);
    if (typeof ref === 'function') ref(element); else if (ref) ref.current = element;
  }, [ref]);

  useLayoutEffect(() => {
    if (!node) return undefined;
    const place = () => {
      const rect = anchorRef?.current?.getBoundingClientRect?.();
      if (!rect) return;
      setPosition(floatingPosition(rect, { width: node.offsetWidth, height: node.offsetHeight }, { width: window.innerWidth, height: window.innerHeight }, align));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [node, anchorRef, align]);

  return createPortal(
    <div ref={attach} data-testid={testId} className={`ba-floating ${className || ''}`}
      style={{ position: 'fixed', right: 'auto', bottom: 'auto', top: position?.top ?? 0, left: position?.left ?? 0, visibility: position ? 'visible' : 'hidden' }}>
      {children}
    </div>,
    document.body,
  );
});
FloatingPanel.displayName = 'FloatingPanel';

export default FloatingPanel;
